import React, { Component } from 'react';
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Stack,
    IconButton,
    Alert,
    TextField
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import { SearchSelect } from '../../components/searchSelect';
import i18n from '../../i18n';
import AmountInput from '../editors/components/AmountInput';
import { hasError } from '../../utils/formatError';

export default class TransferBalanceDialog extends Component {
    constructor(props) {
        super(props);

        const initialMerchantId = props.defaultMerchantId;

        this.state = {
            fromId: null,
            toId: null,
            balance: null,
            submitting: false,
            error: '',
            initialized: false,
            merchantId: initialMerchantId,
            prefilter: this.buildPrefilter(
                initialMerchantId,
                props.prefilterRequest
            )
        };
    }
    buildPrefilter = (merchantId, basePrefilter) => {
        const base = Array.isArray(basePrefilter) ? [...basePrefilter] : [];

        const withoutMerchant = base.filter(
            (f) => f && f.field !== 'merchant.id' && f.field !== 'merchantId'
        );

        if (merchantId !== null && merchantId !== undefined) {
            withoutMerchant.push({
                field: 'merchant.id',
                op: '=',
                value: merchantId
            });
        }

        return withoutMerchant;
    };

    componentDidUpdate(prevProps) {
        if (this.props.open && !prevProps.open && !this.state.initialized) {
            const {
                defaultFromId,
                defaultToId,
                defaultMerchantId,
                prefilterRequest
            } = this.props;

            const merchantId =
            typeof defaultMerchantId === 'number' ||
            typeof defaultMerchantId === 'string'
                ? defaultMerchantId
                : null;

            const fromId =
                typeof defaultFromId === 'number' ||
                typeof defaultFromId === 'string'
                    ? defaultFromId
                    : null;

            const toId =
                typeof defaultToId === 'number' ||
                typeof defaultToId === 'string'
                    ? defaultToId
                    : null;

            this.setState({
                fromId,
                toId,
                merchantId,
                prefilter: this.buildPrefilter(
                    defaultMerchantId,
                    prefilterRequest
                ),
                initialized: true,
                error: ''
            });
        }

        if (!this.props.open && prevProps.open) {
            this.setState({
                initialized: false,
                error: '',
                submitting: false,
                fromId: null,
                toId: null,
                balance: null,
                merchantId: this.props.defaultMerchantId,
                prefilter: this.buildPrefilter(
                    this.props.defaultMerchantId,
                    this.props.prefilterRequest
                )
            });
        }
    }

    validate() {
        const { fromId, toId, balance } = this.state;

        if (fromId === undefined || fromId === null)
            return i18n.t('errors.selectSourceTerminal');
        if (toId === undefined || toId === null)
            return i18n.t('errors.selectDestinationTerminal');
        if (String(fromId) === String(toId))
            return i18n.t('errors.differentTerminals');

        const balNum = Number(balance);
        if (isNaN(balNum) || balNum <= 0) return i18n.t('errors.validBalance');

        return '';
    }

    async handleSubmit() {
        const err = this.validate();
        if (err) {
            this.setState({ error: err });
            return;
        }

        const { server } = this.props;
        const { fromId, toId, balance } = this.state;

        this.setState({ submitting: true, error: '' });

        try {
            const res = await server.transferBalance({
                fromTerminalId: fromId,
                toTerminalId: toId,
                balance
            });

            if (hasError(res)) {
                throw new Error(res.message);
            }

            this.setState({ submitting: false });
            this.props.onClose();
        } catch (e) {
            const message = e?.message || i18n.t('errors.unexpectedError');
            this.setState({ submitting: false, error: message });
        }
    }

    handleUpdateMerchant = (merchantId) => {
        const prefilter = this.buildPrefilter(
            merchantId,
            this.props.prefilterRequest
        );

        this.setState({
            merchantId,
            fromId: null,
            toId: null,
            prefilter
        });
    };


    render() {
        const { open, onClose, searchFields, prefilterRequest } = this.props;
        const {
            fromId,
            toId,
            balance,
            submitting,
            error,
            merchantId,
            prefilter
        } = this.state;

        const terminalsEnabled = Boolean(merchantId);

        return (
            <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
                <DialogTitle
                    sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                >
                    <SwapHorizIcon fontSize="small" />
                    {this.props.title || 'Transfer Balance Between Terminals'}
                    <Box sx={{ flexGrow: 1 }} />
                    <IconButton onClick={onClose} size="small">
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </DialogTitle>

                <DialogContent dividers>
                    <Stack spacing={2}>
                        {/* Merchant */}
                        <SearchSelect
                            id="merchantId"
                            label={"merchant"}
                            searchController="publicMerchants"
                            searchFields={['name', 'email']}
                            value={merchantId}
                            onChange={(_, id) => this.handleUpdateMerchant(id)}
                            prefilterRequest={prefilterRequest}
                        />

                        {/* FROM */}
                        {terminalsEnabled ? (
                            <SearchSelect
                                id="fromId"
                                key={`from-${merchantId ?? 'none'}`}
                                label={"fromTerminal"}
                                searchController="publicMerchantTerminals"
                                searchFields={searchFields}
                                value={fromId}
                                disabled={Boolean(submitting || !merchantId)}
                                onChange={(_, id) =>
                                    this.setState({ fromId: id })
                                }
                                prefilterRequest={prefilter}
                            />
                        ) : (
                            <TextField
                                label={i18n.t('columns.fromTerminal')}
                                fullWidth
                                disabled
                                value=""
                            />
                        )}

                        {/* TO */}
                        {terminalsEnabled ? (
                            <SearchSelect
                                id="toId"
                                key={`to-${merchantId ?? 'none'}`}
                                label="toTerminal"
                                searchController="publicMerchantTerminals"
                                searchFields={searchFields}
                                value={toId}
                                disabled={Boolean(submitting || !merchantId)}
                                onChange={(_, id) =>
                                    this.setState({ toId: id })
                                }
                                prefilterRequest={prefilter}
                            />
                        ) : (
                            <TextField
                                label={i18n.t('columns.toTerminal')}
                                fullWidth
                                disabled
                                value=""
                            />
                        )}

                        <AmountInput
                            id={'balance'}
                            label={i18n.t('columns.balanceToTransfer')}
                            fullWidth
                            value={balance}
                            onChange={(_, val) =>
                                this.setState({ balance: val })
                            }
                        />

                        {error ? <Alert severity="error">{error}</Alert> : null}
                    </Stack>
                </DialogContent>

                <DialogActions>
                    <Button onClick={onClose} disabled={submitting}>
                        {i18n.t('buttons.cancel')}
                    </Button>
                    <Button
                        variant="contained"
                        onClick={() => this.handleSubmit()}
                        disabled={submitting}
                    >
                        {submitting ? (
                            <CircularProgress size={20} />
                        ) : (
                            i18n.t('buttons.transfer')
                        )}
                    </Button>
                </DialogActions>
            </Dialog>
        );
    }
}
